<template>
  <a-drawer
    :title="recordJson ? '编辑推广链接' : '新建推广链接'"
    width="600"
    :visible="visible"
    :closable="false"
    :footer-style="{ textAlign: 'right' }"
    @close="onClose"
  >
    <!-- form -->
    <a-form
      ref="RefForm"
      :model="formState"
      :label-col="{ span: 6 }"
      :wrapper-col="{ span: 16 }"
    >
      <!-- 名称 -->
      <a-form-item
        label="名称"
        name="name"
        :rules="[{ required: true, message: '请输入名称' }]"
      >
        <a-input
          v-model:value="formState.name"
          placeholder="请输入"
        />
      </a-form-item>

      <!-- 跳转类型 -->
      <a-form-item
        label="跳转类型"
        name="page_where"
        :rules="[{ required: true, message: '请选择跳转类型' }]"
      >
        <a-select
          v-model:value="formState.page_where"
          placeholder="请选择"
        >
          <a-select-option
            v-for="item in LandingTypes"
            :key="item.value"
            :value="item.value"
          >
            {{ item.text }}
          </a-select-option>
        </a-select>
      </a-form-item>

      <!-- 界面语种 -->
      <a-form-item
        label="界面语种"
        name="lang_id"
        :rules="[{ required: true, message: '请选择界面语种' }]"
      >
        <a-select
          v-model:value="formState.lang_id"
          placeholder="请选择"
          show-search
          :filter-option="filterOption"
          @change="onLangChange"
        >
          <a-select-option
            v-for="item in languages"
            :key="`${item.id}-${item.name}`"
            :value="item.id"
          >
            {{ item.name }}
          </a-select-option>
        </a-select>
      </a-form-item>

      <!-- 剧集 -->
      <a-form-item
        label="剧集"
        name="lang_drama_id"
        :rules="[{ required: true, message: '请选择剧集' }]"
      >
        <a-select
          v-model:value="formState.lang_drama_id"
          placeholder="请先选择界面语种"
          show-search
          allow-clear
          :filter-option="filterOption"
          :disabled="!formState.lang_id"
          :loading="isDramaLoading"
        >
          <a-select-option
            v-for="item in dramaList"
            :key="`${item.id}-${item.name}`"
            :value="item.id"
          >
            {{ item.name }}
          </a-select-option>
        </a-select>
      </a-form-item>

      <!-- 跳转集数 -->
      <a-form-item
        label="跳转集数"
        name="eq_number"
        :rules="[{ required: true, message: '请输入跳转集数' }]"
      >
        <a-input-number
          v-model:value="formState.eq_number"
          :min="1"
          placeholder="请输入"
          style="width: 100%"
        />
      </a-form-item>

      <!-- 推广渠道 -->
      <a-form-item
        label="推广渠道"
        name="channel"
        :rules="[{ required: true, message: '请选择推广渠道' }]"
      >
        <a-select
          v-model:value="formState.channel"
          placeholder="请选择"
          :disabled="!!recordJson"
        >
          <a-select-option
            v-for="item in PromotionLinkChannels"
            :key="item.value"
            :value="item.value"
          >
            {{ item.text }}
          </a-select-option>
        </a-select>
      </a-form-item>

      <!-- 服务商 -->
      <a-form-item
        label="服务商"
        name="reseller_id"
        :rules="[{ required: true, message: '请选择服务商' }]"
      >
        <a-select
          v-model:value="formState.reseller_id"
          placeholder="请选择"
          allow-clear
        >
          <a-select-option
            v-for="item in resellerList"
            :key="item.id"
            :value="item.id"
          >
            {{ item.name }}
          </a-select-option>
        </a-select>
      </a-form-item>
    </a-form>
    <!-- 尾部 -->
    <template #footer>
      <a-button style="margin-right: 8px" @click="onClose">取消</a-button>
      <a-button type="primary" @click="touchSubmit">确定</a-button>
    </template>
    <!-- 加载 -->
    <Loading :loading="isLoading"></Loading>
  </a-drawer>
</template>

<script setup>
import { ref, nextTick } from 'vue'
import Loading from '@/components/Loading'
import { message } from 'ant-design-vue'
import { LandingTypes, PromotionLinkChannels } from '@/utils/constantList'
import { langSearchList, dramaAll, popularizeDramaSave, resellerAll } from '@/api/admin'

// emit
const emit = defineEmits(['success'])
// form
const RefForm = ref(null)
// 显示
let visible = ref(false)
// 加载
let isLoading = ref(false)
// 剧集加载
let isDramaLoading = ref(false)
// 编辑记录
let recordJson = ref(null)
// 语种列表
let languages = ref([])
// 剧集列表
let dramaList = ref([])
// 服务商列表（待接口）
let resellerList = ref([])

// 表单参数
const formState = ref({
  name: undefined,
  page_where: undefined,
  lang_id: undefined,
  lang_drama_id: undefined,
  eq_number: 1,
  channel: undefined,
  reseller_id: undefined
})

// 模糊匹配
function filterOption(input, option) {
  return `${option.key}`.toLowerCase().includes(`${input || ''}`.toLowerCase())
}

// 语种切换：清空剧集选择，重新加载剧集列表
function onLangChange(langId) {
  formState.value.lang_drama_id = undefined
  dramaList.value = []
  if (langId) {
    getDramaList(langId)
  }
}

// 获取语种列表
function getLanguageList() {
  langSearchList().then(res => {
    const { code, data } = res
    if (code === 0) {
      languages.value = data
    }
  })
}

// 获取服务商列表
function getResellerList() {
  resellerAll().then(res => {
    const { code, data } = res
    if (code === 0) {
      resellerList.value = data || []
    }
  })
}

// 根据语种获取剧集列表
function getDramaList(langId) {
  isDramaLoading.value = true
  dramaAll({ lang_id: langId }).then(res => {
    isDramaLoading.value = false
    const { code, data } = res
    if (code === 0) {
      dramaList.value = data || []
    } else {
      dramaList.value = []
    }
  }).catch(() => {
    isDramaLoading.value = false
    dramaList.value = []
  })
}

// 显示弹窗（record 有值则为编辑模式）
function showDrawer(record) {
  visible.value = true
  recordJson.value = record || null
  getLanguageList()
  getResellerList()

  nextTick(() => {
    if (record && record.id) {
      // 编辑模式：填充表单
      formState.value = {
        name: record.name,
        page_where: record.page_where,
        lang_id: record.lang_id,
        lang_drama_id: record.lang_drama_id,
        eq_number: record.eq_number,
        channel: record.channel,
        reseller_id: record.reseller_id
      }
      // 加载剧集列表
      if (record.lang_id) {
        getDramaList(record.lang_id)
      }
    } else {
      // 新建模式：重置表单
      formState.value = {
        name: undefined,
        page_where: undefined,
        lang_id: undefined,
        lang_drama_id: undefined,
        eq_number: 1,
        channel: undefined,
        reseller_id: undefined
      }
      dramaList.value = []
    }
  })
}

// 关闭弹窗
function onClose() {
  visible.value = false
  RefForm.value?.resetFields()
  formState.value = {
    name: undefined,
    page_where: undefined,
    lang_id: undefined,
    lang_drama_id: undefined,
    eq_number: 1,
    channel: undefined,
    reseller_id: undefined
  }
  dramaList.value = []
  recordJson.value = null
}

// 提交
function touchSubmit() {
  RefForm.value.validate().then(() => {
    const params = {
      id: recordJson.value?.id,
      name: formState.value.name,
      page_where: formState.value.page_where,
      lang_id: formState.value.lang_id,
      lang_drama_id: formState.value.lang_drama_id,
      eq_number: formState.value.eq_number,
      channel: formState.value.channel,
      reseller_id: formState.value.reseller_id
    }
    isLoading.value = true
    popularizeDramaSave(params).then(res => {
      isLoading.value = false
      if (res.code === 0) {
        message.success('保存成功')
        onClose()
        emit('success')
      } else {
        message.error(res.message || '保存失败')
      }
    }).catch(() => {
      isLoading.value = false
      message.error('保存失败')
    })
  }).catch(err => {
    console.log(err)
  })
}

// 暴露方法给父组件
defineExpose({
  showDrawer
})
</script>

<style scoped>
/* 可以添加自定义样式 */
</style>
